import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  IconEdit,
  IconFilter,
  IconArrowUp,
  IconArrowDown,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../../utils/dateFormat';
import type { Survey } from '../types';
import { isCardSurvey } from '../types';

type SortKey = 'id' | 'name' | 'description' | 'data_reward' | 'brand_id' | 'questions' | 'created_at' | 'updated_at' | 'is_active';
type SortDirection = 'default' | 'asc' | 'desc';

interface SurveyListProps {
  surveys: Survey[];
  statusUpdatingId: number | null;
  onEditSurvey: (survey: Survey) => void;
  onToggleActive: (survey: Survey, newValue: boolean) => void;
}

function getSurveySortValue(survey: Survey, key: SortKey): number | string {
  switch (key) {
    case 'id':
      return survey.id;
    case 'name':
      return (survey.name ?? '').toLowerCase();
    case 'description':
      return (survey.description ?? '').toLowerCase();
    case 'data_reward': {
      const v = survey.data_reward;
      if (v === null || v === undefined || v === '') return -1;
      const n = Number(v);
      return Number.isFinite(n) ? n : -1;
    }
    case 'brand_id': {
      const v = survey.brand_id;
      return v == null || v === '' ? '' : String(v).toLowerCase();
    }
    case 'questions': {
      const q = survey.questions;
      if (isCardSurvey(q)) {
        return (q.questions?.length ?? 0) * 1000 + (q.paths?.length ?? 0);
      }
      const nodes = q?.nodes?.length ?? 0;
      const edges = q?.edges?.length ?? 0;
      return nodes * 1000 + edges;
    }
    case 'created_at':
      return new Date(survey.created_at).getTime();
    case 'updated_at':
      return new Date(survey.updated_at).getTime();
    case 'is_active':
      return (survey.is_active === true || (typeof survey.is_active === 'number' && survey.is_active !== 0)) ? 1 : 0;
    default:
      return '';
  }
}

export function SurveyList({
  surveys,
  statusUpdatingId,
  onEditSurvey,
  onToggleActive,
}: SurveyListProps) {
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('default');

  const [statusActive, setStatusActive] = useState(true);
  const [statusInactive, setStatusInactive] = useState(false);
  const [brandIdChecked, setBrandIdChecked] = useState<Record<string, boolean>>({});
  const [dataRewardMin, setDataRewardMin] = useState<string>('');
  const [dataRewardMax, setDataRewardMax] = useState<string>('');
  const [dataRewardExact, setDataRewardExact] = useState<string>('');

  const handleRowClick = (survey: Survey) => {
    navigate(`/surveys/${survey.id}`);
  };

  const handleEditClick = (e: React.MouseEvent, survey: Survey) => {
    e.stopPropagation();
    onEditSurvey(survey);
  };

  const cycleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('default');
    } else {
      setSortDirection('asc');
    }
  };

  const uniqueBrandIds = useMemo(() => {
    const set = new Set<string>();
    surveys.forEach((s) => {
      const v = s.brand_id;
      if (v == null || v === '') set.add('');
      else set.add(String(v).trim());
    });
    return Array.from(set).sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
  }, [surveys]);

  const filteredSurveys = useMemo(() => {
    let result = surveys;
    if (statusActive !== statusInactive) {
      if (statusActive && !statusInactive) {
        result = result.filter((s) => s.is_active === true || (typeof s.is_active === 'number' && s.is_active !== 0));
      } else if (!statusActive && statusInactive) {
        result = result.filter((s) => s.is_active === false || (typeof s.is_active === 'number' && s.is_active === 0) || s.is_active == null);
      }
    }
    const checkedBrands = uniqueBrandIds.filter((id) => brandIdChecked[id] !== false);
    if (checkedBrands.length < uniqueBrandIds.length) {
      const set = new Set(checkedBrands);
      result = result.filter((s) => {
        const v = s.brand_id;
        if (v == null || v === '') return set.has('');
        return set.has(String(v).trim());
      });
    }
    const exactN = dataRewardExact.trim() !== '' ? parseFloat(dataRewardExact.trim()) : NaN;
    const minN = dataRewardMin.trim() !== '' ? parseFloat(dataRewardMin.trim()) : NaN;
    const maxN = dataRewardMax.trim() !== '' ? parseFloat(dataRewardMax.trim()) : NaN;
    if (Number.isFinite(exactN)) {
      result = result.filter((s) => {
        const v = s.data_reward;
        if (v == null || v === '') return false;
        const n = Number(v);
        return Number.isFinite(n) && n === exactN;
      });
    } else if (Number.isFinite(minN) || Number.isFinite(maxN)) {
      result = result.filter((s) => {
        const v = s.data_reward;
        if (v == null || v === '') return false;
        const n = Number(v);
        if (!Number.isFinite(n)) return false;
        if (Number.isFinite(minN) && n < minN) return false;
        if (Number.isFinite(maxN) && n > maxN) return false;
        return true;
      });
    }
    return result;
  }, [surveys, statusActive, statusInactive, brandIdChecked, uniqueBrandIds, dataRewardExact, dataRewardMin, dataRewardMax]);

  const sortedSurveys = useMemo(() => {
    const list = [...filteredSurveys];
    const key = sortKey ?? 'id';
    const dir = sortDirection;
    if (dir === 'default') {
      return list.sort((a, b) => a.id - b.id);
    }
    list.sort((a, b) => {
      const va = getSurveySortValue(a, key);
      const vb = getSurveySortValue(b, key);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [filteredSurveys, sortKey, sortDirection]);

  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) return null;
    if (sortDirection === 'default') return null;
    return sortDirection === 'asc' ? <IconArrowUp className="size-4" /> : <IconArrowDown className="size-4" />;
  };

  const SortableHeader = ({
    label,
    sk,
    filterContent,
  }: {
    label: string;
    sk: SortKey;
    filterContent?: React.ReactNode;
  }) => (
    <TableHead className="select-none">
      <div className="flex flex-nowrap items-center gap-0.5">
        <span
          onClick={() => cycleSort(sk)}
          className="flex cursor-pointer items-center gap-0.5"
        >
          {label}
          {renderSortIcon(sk)}
        </span>
        {filterContent && filterContent}
      </div>
    </TableHead>
  );

  const StatusFilter = (
    <Popover>
      <PopoverTrigger
        title="Filter by Status"
        className={cn(buttonVariants({ variant: 'ghost', size: 'icon-xs' }))}
      >
        <IconFilter className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent className="w-56">
        <p className="mb-1 text-sm font-semibold">Status</p>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Checkbox id="filter-active" checked={statusActive} onCheckedChange={(v) => setStatusActive(!!v)} />
            <Label htmlFor="filter-active">Active</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="filter-inactive" checked={statusInactive} onCheckedChange={(v) => setStatusInactive(!!v)} />
            <Label htmlFor="filter-inactive">Inactive</Label>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  const BrandFilter = (
    <Popover>
      <PopoverTrigger
        title="Filter by Brand ID"
        className={cn(buttonVariants({ variant: 'ghost', size: 'icon-xs' }))}
      >
        <IconFilter className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent className="w-56">
        <p className="mb-1 text-sm font-semibold">Brand ID</p>
        <div className="flex max-h-[280px] flex-col gap-2 overflow-y-auto">
          {uniqueBrandIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">No brands in data</p>
          ) : (
            uniqueBrandIds.map((id) => (
              <div key={id || '__empty__'} className="flex items-center gap-2">
                <Checkbox
                  id={`brand-${id || '__empty__'}`}
                  checked={brandIdChecked[id] !== false}
                  onCheckedChange={(v) => setBrandIdChecked((prev) => ({ ...prev, [id]: !!v }))}
                />
                <Label htmlFor={`brand-${id || '__empty__'}`}>{id === '' ? '(No brand)' : id}</Label>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  const DataRewardFilter = (
    <Popover>
      <PopoverTrigger
        title="Filter by Data Reward"
        className={cn(buttonVariants({ variant: 'ghost', size: 'icon-xs' }))}
      >
        <IconFilter className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent className="w-56">
        <p className="mb-1 text-sm font-semibold">Data Reward</p>
        <Input
          type="number"
          value={dataRewardExact}
          onChange={(e) => setDataRewardExact(e.target.value)}
          placeholder="Exact amount (e.g. 20)"
          className="mb-1"
        />
        <p className="mb-0.5 block text-xs text-muted-foreground">Or range:</p>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={dataRewardMin}
            onChange={(e) => setDataRewardMin(e.target.value)}
            placeholder="Min"
            className="flex-1"
          />
          <Input
            type="number"
            value={dataRewardMax}
            onChange={(e) => setDataRewardMax(e.target.value)}
            placeholder="Max"
            className="flex-1"
          />
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="w-full overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader label="ID" sk="id" />
            <SortableHeader label="Name" sk="name" />
            <SortableHeader label="Description" sk="description" />
            <SortableHeader label="Data Reward" sk="data_reward" filterContent={DataRewardFilter} />
            <SortableHeader label="Brand ID" sk="brand_id" filterContent={BrandFilter} />
            <SortableHeader label="Questions" sk="questions" />
            <SortableHeader label="Created" sk="created_at" />
            <SortableHeader label="Updated" sk="updated_at" />
            <SortableHeader label="Status" sk="is_active" filterContent={StatusFilter} />
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedSurveys.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10}>
                <p className="py-3 text-center text-sm text-muted-foreground">
                  {filteredSurveys.length === 0 && surveys.length > 0
                    ? 'No surveys match the current filters.'
                    : 'No surveys to show.'}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            sortedSurveys.map((survey) => (
              <TableRow
                key={survey.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(survey)}
              >
                <TableCell>
                  <span className="text-sm text-muted-foreground">{survey.id}</span>
                </TableCell>
                <TableCell>
                  <span
                    className="max-w-[200px] truncate text-sm font-medium"
                    title={survey.name}
                  >
                    {survey.name}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    className="max-w-[250px] truncate text-sm text-muted-foreground"
                    title={survey.description || undefined}
                  >
                    {survey.description || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {survey.data_reward === null ||
                    survey.data_reward === undefined ||
                    survey.data_reward === ''
                      ? '-'
                      : survey.data_reward}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {survey.brand_id === null ||
                    survey.brand_id === undefined ||
                    survey.brand_id === ''
                      ? '-'
                      : survey.brand_id}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {isCardSurvey(survey.questions)
                      ? `${survey.questions.questions?.length ?? 0} questions, ${survey.questions.paths?.length ?? 0} paths`
                      : `${survey.questions?.nodes?.length ?? 0} nodes, ${survey.questions?.edges?.length ?? 0} edges`}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{formatDate(survey.created_at)}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{formatDate(survey.updated_at)}</span>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={statusUpdatingId === survey.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleActive(survey, !(survey.is_active ?? true));
                    }}
                    className={cn(
                      'rounded-full px-3 font-semibold',
                      (survey.is_active ?? true)
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                        : 'bg-gray-200 text-foreground hover:bg-gray-300'
                    )}
                  >
                    {(survey.is_active ?? true) ? 'Active' : 'Inactive'}
                  </Button>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={(e) => handleEditClick(e, survey)}
                    title="Edit Survey"
                  >
                    <IconEdit className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
